import { useEffect, useRef, useState, type ReactNode } from 'react'

import { valueSwap } from '../../lib/motion'

/**
 * 13c · SYNC, WITHOUT A SPINNER — one value being replaced.
 *
 * The stale value lifts out (`opacity 1→0, translateY(-4px)`), the fresh one
 * rises in (`translateY(5px)→0`). A spinner says "wait"; this says WHAT ARRIVED.
 *
 * THE RULE THIS COMPONENT EXISTS TO KEEP IS ABOUT WHAT DOES *NOT* MOVE.
 * "Only values that changed animate. A row Canvas didn't send must be visibly
 * untouched — that stillness is how the user knows what's new." So the swap is
 * keyed off the value itself: mount it on every row of a synced list, and the
 * rows that came back identical never animate, because their value never
 * changed. Nothing upstream has to remember which rows to leave alone, which is
 * the version of this that goes wrong the first time a caller forgets.
 *
 * Hidden grades stay hidden and stay still: a withheld value is not a value that
 * changed, so it never enters here at all (13f owns that state).
 *
 * FIRST PAINT IS NOT A SWAP. A value arriving because the screen just mounted is
 * not news, so the initial render is silent.
 */
export function ValueSwap({
  value,
  children,
  className,
}: {
  /** The comparison key. A swap runs when and only when this changes. */
  value: string | number
  /** How the current value renders. */
  children: ReactNode
  className?: string
}) {
  const [stale, setStale] = useState<ReactNode | null>(null)
  const previous = useRef<{ value: string | number; node: ReactNode }>({ value, node: children })
  const staleRef = useRef<HTMLSpanElement>(null)
  const freshRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (previous.current.value === value) {
      previous.current.node = children
      return
    }
    setStale(previous.current.node)
    previous.current = { value, node: children }
  }, [value, children])

  useEffect(() => {
    if (stale === null) return
    const run = valueSwap(staleRef.current, freshRef.current)
    let abandoned = false
    void run.finished.then(() => {
      if (!abandoned) setStale(null)
    })
    return () => {
      abandoned = true
      run.cancel()
    }
  }, [stale])

  return (
    <span className={`relative inline-block ${className ?? ''}`}>
      {/* The departing value is taken OUT of flow so the fresh one does not jump sideways as it
          leaves. Both are right-anchored, which is where every value this wraps already sits. */}
      {stale !== null && (
        <span ref={staleRef} aria-hidden className="pointer-events-none absolute inset-y-0 right-0">
          {stale}
        </span>
      )}
      <span ref={freshRef} className="block">
        {children}
      </span>
    </span>
  )
}
